'use client';

import { useState, useCallback, useRef } from 'react';
export type AdoAuthErrorType = 'expired' | 'no_access' | 'no_refresh_token' | 'popup_blocked' | 'unknown';
export interface AdoAuthError {
  type: AdoAuthErrorType;
  message: string;
  action: string;
}
export function useAdoAuth() {
  const [adoToken, setAdoToken] = useState<string | null>(null);
  const [isAcquiring, setIsAcquiring] = useState(false);
  const [authError, setAuthError] = useState<AdoAuthError | null>(null);
  const expiresAtRef = useRef<number>(0);
  const acquireAdoToken = useCallback(async (): Promise<string | null> => {
    // Return cached token if still valid (with 60s buffer)
    if (adoToken && Date.now() < expiresAtRef.current - 60_000) {
      return adoToken;
    }
    setIsAcquiring(true);
    setAuthError(null);
    try {
      const res = await fetch('/api/auth/ado-token', {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) {
        const code = data.error as string | undefined;
        const description = (data.description ?? data.error ?? 'Could not acquire an Azure DevOps token.') as string;
        setAuthError({
          type: code === 'no_refresh_token' ? 'no_refresh_token' : code === 'unauthenticated' ? 'no_access' : 'no_access',
          message: description,
          action: code === 'no_refresh_token' ? 'Sign out and sign back in' : 'Try again'
        });
        setAdoToken(null);
        return null;
      }
      setAdoToken(data.access_token);
      expiresAtRef.current = Date.now() + (data.expires_in ?? 3600) * 1000;
      return data.access_token;
    } catch (err) {
      setAuthError({
        type: 'unknown',
        message: err instanceof Error ? err.message : String(err),
        action: 'Try again'
      });
      setAdoToken(null);
      return null;
    } finally {
      setIsAcquiring(false);
    }
  }, [adoToken]);
  return {
    adoToken,
    acquireAdoToken,
    isAcquiring,
    authError
  };
}